/**
 * Image Upload Service with WebP Conversion
 *
 * Handles image upload to Cloudflare R2 Storage with automatic
 * conversion to WebP format for optimal compression and performance.
 */

import { getAuth } from 'firebase/auth';

interface UploadResult {
  url: string;
  filename: string;
}

/**
 * Convert image file to WebP format with optional resizing
 * @param file - Original image file
 * @param options - Compression and resizing options
 * @returns WebP blob
 */
export async function convertToWebP(
  file: File,
  options: {
    quality?: number;
    maxWidth?: number;
    maxHeight?: number;
  } = {}
): Promise<Blob> {
  const { quality = 0.8, maxWidth = 1920, maxHeight = 1920 } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Não foi possível criar o contexto do canvas'));
      return;
    }

    img.onload = () => {
      let width = img.naturalWidth;
      let height = img.naturalHeight;

      // Calculate new dimensions maintain aspect ratio
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;

      // Enable image smoothing for better quality when resizing
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Draw image on canvas with new dimensions
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to WebP
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Falha ao converter imagem para WebP'));
          }
        },
        'image/webp',
        quality
      );

      // Cleanup
      URL.revokeObjectURL(img.src);
    };

    img.onerror = () => {
      reject(new Error('Falha ao carregar a imagem'));
      URL.revokeObjectURL(img.src);
    };

    // Load image from file
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Generate a unique filename for the uploaded image
 */
function generateFilename(originalName: string): string {
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 15);
  const baseName = originalName.replace(/\.[^/.]+$/, ''); // Remove extension
  const safeName = baseName
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .substring(0, 50);

  return `${safeName}-${timestamp}-${randomStr}.webp`;
}

/**
 * Upload image to Cloudflare R2 Storage
 * @param file - Image file to upload
 * @param options - Upload options
 * @returns Upload result with URL and filename
 */
export async function uploadImageToR2(
  file: File,
  options: {
    quality?: number;
    maxWidth?: number;
    maxHeight?: number;
    onProgress?: (progress: number) => void;
  } = {}
): Promise<UploadResult> {
  const { quality = 0.8, onProgress } = options;

  try {
    // Step 1: Convert to WebP (show 30% progress)
    onProgress?.(30);
    const webpBlob = await convertToWebP(file, {
      quality,
      maxWidth: options.maxWidth,
      maxHeight: options.maxHeight,
    });

    // Step 2: Generate filename
    const filename = generateFilename(file.name);

    // Step 3: Get authentication token
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('Usuário não autenticado');
    }

    const token = await user.getIdToken();

    // Step 4: Upload to server (which will forward to R2)
    onProgress?.(60);

    const formData = new FormData();
    formData.append('file', webpBlob, filename);
    formData.append('originalName', file.name);

    // TODO: Replace with your actual API endpoint
    // This endpoint should be a Cloudflare Worker or API route that handles R2 upload
    const apiBase = import.meta.env.VITE_API_ENDPOINT;
    const API_ENDPOINT =
      import.meta.env.VITE_R2_UPLOAD_ENDPOINT ||
      (apiBase ? `${apiBase}/api/upload` : '/api/upload');

    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    onProgress?.(90);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(error.message || 'Falha ao fazer upload da imagem');
    }

    const result = await response.json();

    onProgress?.(100);

    return {
      url: result.url,
      filename: result.filename || filename,
    };
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error instanceof Error ? error : new Error('Erro desconhecido ao fazer upload da imagem');
  }
}

/**
 * Upload multiple images to R2
 * @param files - Array of image files
 * @param options - Upload options
 * @returns Array of upload results
 */
export async function uploadMultipleImages(
  files: File[],
  options: {
    quality?: number;
    maxWidth?: number;
    maxHeight?: number;
    onProgress?: (fileIndex: number, progress: number) => void;
  } = {}
): Promise<UploadResult[]> {
  const { quality = 0.8, onProgress } = options;

  const results: UploadResult[] = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    if (!file) continue;

    const result = await uploadImageToR2(file, {
      quality,
      maxWidth: options.maxWidth,
      maxHeight: options.maxHeight,
      onProgress: (progress) => onProgress?.(i, progress),
    });

    results.push(result);
  }

  return results;
}

/**
 * Validate image file
 * @param file - File to validate
 * @returns true if valid, error message otherwise
 */
export function validateImageFile(file: File): { valid: boolean; error?: string } {
  // Check file type
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
  if (!validTypes.includes(file.type)) {
    return {
      valid: false,
      error: 'Formato de arquivo inválido. Use JPEG, PNG, WebP ou GIF.',
    };
  }

  // Check file size (max 10MB)
  const maxSize = 10 * 1024 * 1024; // 10MB in bytes
  if (file.size > maxSize) {
    return {
      valid: false,
      error: 'Arquivo muito grande. O tamanho máximo é 10MB.',
    };
  }

  return { valid: true };
}
